/**
 * Hooks de query (TanStack) — centraliza queryKeys e wrappers pros endpoints.
 *
 * Mutations não estão aqui — vivem nas próprias páginas pra terem
 * acesso ao queryClient pra invalidar caches específicos.
 */

import { useQuery } from "@tanstack/react-query";

import {
  alertEventsApi,
  alertRulesApi,
  channelsApi,
  deviceHealthApi,
  episodiosApi,
  subscriptionsApi,
  tenantSettingsApi,
  type AlertEventsQuery,
  type DeviceStatusFilters,
  type EpisodiosQuery,
} from "@/api/alerts";
import {
  devicesApi,
  type DeviceListFilters,
} from "@/api/devices";
import { equipmentsApi } from "@/api/equipments";
import { insightsApi } from "@/api/insights";
import { readingsApi, type EquipmentReadingsQuery } from "@/api/readings";
import { reportsApi } from "@/api/reports";
import { sitesApi } from "@/api/sites";
import { tenantsApi } from "@/api/tenants";
import { tenantsCompanyApi } from "@/api/tenantsCompany";
import { usersApi } from "@/api/users";
import type { ReportListFilters } from "@/types";

// --- Query keys --------------------------------------------------------------

export const qk = {
  tenants: () => ["tenants"] as const,
  tenant: (id: string) => ["tenant", id] as const,

  sites: (tenantId?: string) => ["sites", { tenantId: tenantId ?? null }] as const,
  site: (id: string) => ["site", id] as const,

  equipments: () => ["equipments"] as const,
  equipmentsInSite: (siteId: string) => ["equipments", "site", siteId] as const,
  equipment: (id: string) => ["equipment", id] as const,
  equipmentSummary: (id: string) => ["equipment", id, "summary"] as const,
  equipmentDevices: (id: string) => ["equipment", id, "devices"] as const,
  equipmentReadings: (id: string, field: string, q?: EquipmentReadingsQuery) =>
    ["equipment", id, "readings", field, q ?? {}] as const,

  devices: (filters?: DeviceListFilters) => ["devices", filters ?? {}] as const,
  device: (id: string) => ["device", id] as const,
  deviceSummary: (id: string) => ["device", id, "summary"] as const,
  orphanDevices: () => ["orphan-devices"] as const,

  users: (tenantId?: string) => ["users", { tenantId: tenantId ?? null }] as const,
  user: (id: string) => ["user", id] as const,

  // Cadastro comercial
  tenantCompany:        (tenantId: string) => ["tenant", tenantId, "company"] as const,
  tenantResponsaveis:   (tenantId: string, includeInactive: boolean) =>
                          ["tenant", tenantId, "responsaveis", { includeInactive }] as const,
  tenantLicencas:       (tenantId: string, includeInactive: boolean) =>
                          ["tenant", tenantId, "licencas", { includeInactive }] as const,
  tenantCompleteness:   (tenantId: string) => ["tenant", tenantId, "completeness"] as const,
  tenantAuditLogs:      (tenantId: string) => ["tenant", tenantId, "audit-logs"] as const,
  cepLookup:            (cep: string) => ["viacep", cep] as const,

  // Reports
  reports:    (filters?: ReportListFilters) => ["reports", filters ?? {}] as const,
  report:     (id: string) => ["report", id] as const,
};

// --- Tenants -----------------------------------------------------------------

export function useTenants(enabled = true) {
  return useQuery({
    queryKey: qk.tenants(),
    queryFn: () => tenantsApi.list(),
    enabled,
  });
}

// --- Sites -------------------------------------------------------------------

export function useSites(params?: { tenant_id?: string; enabled?: boolean }) {
  const { tenant_id, enabled = true } = params ?? {};
  return useQuery({
    queryKey: qk.sites(tenant_id),
    queryFn: () => sitesApi.list(tenant_id ? { tenant_id } : undefined),
    enabled,
  });
}

/**
 * A imagem da planta de uma unidade, pronta pro `<img>`.
 *
 * Existe porque a rota da planta é autenticada e `<img src>` não carrega o
 * Bearer — ver `sitesApi.fetchPlanta`, que também explica por que a imagem
 * vem como `data:` URL e não como object URL.
 *
 * `versao` (o `planta_atualizada_em` da unidade) entra na chave pra que
 * trocar a planta traga a nova — sem isso o usuário sobe o arquivo novo e
 * continua vendo o antigo, e conclui que o upload falhou.
 */
export function usePlantaImagem(
  siteId: string | undefined,
  versao: string | null | undefined,
  habilitada = true,
) {
  const query = useQuery({
    queryKey: ["site", siteId ?? "noop", "planta", versao ?? ""],
    queryFn: () => sitesApi.fetchPlanta(siteId!),
    // Sem `versao` no enabled: unidade com planta cadastrada e data nula
    // (registro antigo, restore parcial) ficaria em carregamento pra sempre,
    // que na tela é um esqueleto cinza que nunca vira nada. Quem sabe se há
    // arquivo é o chamador, e é ele que liga isto.
    enabled: !!siteId && habilitada,
    // A imagem não muda sem o `versao` mudar, e o `versao` já está na chave.
    staleTime: Infinity,
    // Mas NÃO fica pra sempre na memória: cada entrada é a imagem inteira em
    // base64, e quem tem dez unidades passeia por dez delas.
    gcTime: 10 * 60_000,
    // Uma planta que falhou não melhora com insistência, e cada tentativa
    // baixa megabytes.
    retry: 1,
  });

  return {
    url: query.data ?? null,
    carregando: query.isPending,
    erro: query.isError,
  };
}

/** O desenho vetorial da unidade. Fora do `useSites` de propósito — ver
 *  `SiteOut.planta_desenho_atualizado_em`. */
export function usePlantaDesenho(
  siteId: string | undefined,
  versao: string | null | undefined,
  habilitada = true,
) {
  return useQuery({
    queryKey: ["site", siteId ?? "noop", "desenho", versao ?? ""],
    queryFn: () => sitesApi.lerDesenho(siteId!),
    enabled: !!siteId && habilitada,
    staleTime: Infinity,
  });
}

export function useSite(id: string | undefined) {
  return useQuery({
    queryKey: id ? qk.site(id) : ["site", "noop"],
    queryFn: () => sitesApi.get(id!),
    enabled: !!id,
  });
}

// --- Equipments --------------------------------------------------------------

/**
 * Catálogo de tipos de ativo. Estático: cacheia por tempo longo em vez de
 * bater na API a cada abertura do formulário.
 */
export function useEquipmentKinds() {
  return useQuery({
    queryKey: ["equipment-kinds"],
    queryFn: () => equipmentsApi.kinds(),
    staleTime: 60 * 60 * 1000,
  });
}

export function useEquipments(params?: { tenant_id?: string; include_archived?: boolean }) {
  return useQuery({
    queryKey: ["equipments", params ?? {}],
    queryFn: () => equipmentsApi.list({
      ...(params?.tenant_id ? { tenant_id: params.tenant_id } : {}),
      ...(params?.include_archived ? { include_archived: true } : {}),
    }),
  });
}

export function useEquipmentsInSite(siteId: string | undefined) {
  return useQuery({
    queryKey: siteId ? qk.equipmentsInSite(siteId) : ["equipments", "site", "noop"],
    queryFn: () => equipmentsApi.listInSite(siteId!),
    enabled: !!siteId,
  });
}

export function useEquipment(id: string | undefined) {
  return useQuery({
    queryKey: id ? qk.equipment(id) : ["equipment", "noop"],
    queryFn: () => equipmentsApi.get(id!),
    enabled: !!id,
  });
}

export function useEquipmentSummary(
  id: string | undefined,
  options: { refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: id ? qk.equipmentSummary(id) : ["equipment", "noop", "summary"],
    queryFn: () => equipmentsApi.summary(id!),
    enabled: !!id,
    // 10s — tela de detalhe quer ver evento (porta abrindo, temp mudando)
    // rápido. Antes era 5s mas isso somado a /readings polling de 10s
    // saturava o backend. Backend cacheia /summary por 5s, então staleTime
    // 5s evita re-render quando o cache hit retorna dados idênticos.
    refetchInterval: options.refetchInterval ?? 10_000,
    staleTime: 5_000,
  });
}

export function useEquipmentStats(
  id: string | undefined,
  q: { from?: string; to?: string } = {},
) {
  return useQuery({
    queryKey: id
      ? ["equipment", id, "stats", q.from ?? "", q.to ?? ""]
      : ["equipment", "noop", "stats"],
    queryFn: () => equipmentsApi.stats(id!, q),
    enabled: !!id,
    // Backend cacheia a resposta por 60s — refetch mais frequente que isso
    // só consome CPU sem trazer dado novo. 90s dá folga pro cache invalidar.
    refetchInterval: 90_000,
    staleTime: 60_000,
  });
}

export function useTenantStats(
  tenantId: string | undefined,
  // `site_id` restringe a UMA unidade. Ele entra na queryKey porque o mesmo
  // tenant com e sem filtro são perguntas diferentes: sem isso, quem abre a
  // unidade recebe o número do tenant inteiro do cache.
  q: { from?: string; to?: string; site_id?: string } = {},
) {
  return useQuery({
    queryKey: tenantId
      ? ["tenant", tenantId, "stats", q.from ?? "", q.to ?? "", q.site_id ?? ""]
      : ["tenant", "noop", "stats"],
    queryFn: () => tenantsApi.stats(tenantId!, q),
    enabled: !!tenantId,
    refetchInterval: 90_000,
    staleTime: 60_000,
  });
}

// --- envpulse AI / Insights -------------------------------------------------

export function useEquipmentInsight(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: id ? ["equipment", id, "insight"] : ["equipment", "noop", "insight"],
    queryFn: () => insightsApi.equipment(id!),
    enabled: !!id && enabled,
    // Insights mudam só a cada 6h. Refetch a cada 10min é mais que suficiente.
    refetchInterval: 10 * 60_000,
    staleTime: 5 * 60_000,
    // 404 acontece pra equipments recém-cadastrados (worker ainda não rodou).
    // Não vamos retentar agressivamente.
    retry: false,
  });
}

export function useTenantInsightsSummary(tenantId: string | undefined) {
  return useQuery({
    queryKey: tenantId ? ["tenant", tenantId, "insights-summary"] : ["tenant", "noop", "insights"],
    queryFn: () => insightsApi.tenantSummary(tenantId!),
    enabled: !!tenantId,
    refetchInterval: 10 * 60_000,
    staleTime: 5 * 60_000,
  });
}

export function useEquipmentDevices(id: string | undefined) {
  return useQuery({
    queryKey: id ? qk.equipmentDevices(id) : ["equipment", "noop", "devices"],
    queryFn: () => equipmentsApi.devices(id!),
    enabled: !!id,
  });
}

export function useEquipmentReadings(
  id: string | undefined,
  field: string,
  q: EquipmentReadingsQuery,
  enabled = true,
  options: { refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: id ? qk.equipmentReadings(id, field, q) : ["equipment", "noop", "r"],
    queryFn: () => readingsApi.equipmentField(id!, field, q),
    enabled: !!id && enabled,
    // 60s — série histórica do gráfico. Antes era 10s e isso saturava o
    // Postgres (cada ChartStack abre 7+ painéis, cada um chama um campo).
    // Backend cacheia /readings por 15s, staleTime 30s evita re-render
    // desnecessário entre polls. Para eventos rápidos (porta abrindo) o
    // canal certo é /summary + /active-alerts, não o gráfico.
    refetchInterval: options.refetchInterval ?? 60_000,
    staleTime: 30_000,
  });
}

// --- Devices -----------------------------------------------------------------

export function useDevices(filters?: DeviceListFilters) {
  return useQuery({
    queryKey: qk.devices(filters),
    queryFn: () => devicesApi.list(filters),
  });
}

export function useDevice(id: string | undefined) {
  return useQuery({
    queryKey: id ? qk.device(id) : ["device", "noop"],
    queryFn: () => devicesApi.get(id!),
    enabled: !!id,
  });
}

export function useDeviceSummary(
  id: string | undefined,
  options: { refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: id ? qk.deviceSummary(id) : ["device", "noop", "summary"],
    queryFn: () => devicesApi.summary(id!),
    enabled: !!id,
    refetchInterval: options.refetchInterval ?? 30_000,
  });
}

export function useOrphanDevices() {
  return useQuery({
    queryKey: qk.orphanDevices(),
    queryFn: () => devicesApi.orphans(),
  });
}

// --- Users -------------------------------------------------------------------

export function useUsers(params?: { tenant_id?: string; enabled?: boolean }) {
  const { tenant_id, enabled = true } = params ?? {};
  return useQuery({
    queryKey: qk.users(tenant_id),
    queryFn: () => usersApi.list(tenant_id ? { tenant_id } : undefined),
    enabled,
  });
}

// --- Alertas + saúde --------------------------------------------------------

export const alertQk = {
  tenantSettings: (tenantId?: string) => ["tenant-alert-settings", tenantId ?? null] as const,
  rules:          (tenantId?: string) => ["alert-rules", tenantId ?? null] as const,
  channels:       (tenantId?: string) => ["notification-channels", tenantId ?? null] as const,
  subscriptions:  (channelId?: string) => ["alert-subscriptions", channelId ?? null] as const,
  events:         (q?: AlertEventsQuery) => ["alert-events", q ?? {}] as const,
  active:         (tenantId?: string) => ["active-alerts", tenantId ?? null] as const,
  activeSite:     (tenantId?: string) => ["active-site-alerts", tenantId ?? null] as const,
  deviceHealth:   (deviceId: string) => ["device-health", deviceId] as const,
};

export function useTenantAlertSettings(tenantId?: string, enabled = true) {
  return useQuery({
    queryKey: alertQk.tenantSettings(tenantId),
    // signal deixa React Query cancelar automaticamente a request se o
    // componente desmontar ou o tenantId trocar antes de terminar
    // (evita race quando super_admin troca de tenant rápido no dropdown).
    queryFn: ({ signal }) => tenantSettingsApi.get(tenantId, signal),
    enabled,
  });
}

export function useAlertRules(tenantId?: string) {
  return useQuery({
    queryKey: alertQk.rules(tenantId),
    queryFn: () => alertRulesApi.list(tenantId),
  });
}

export function useNotificationChannels(tenantId?: string, enabled = true) {
  return useQuery({
    queryKey: alertQk.channels(tenantId),
    queryFn: ({ signal }) => channelsApi.list(tenantId, signal),
    enabled,
  });
}

export function useAlertSubscriptions(channelId?: string, tenantId?: string) {
  return useQuery({
    queryKey: ["alert-subscriptions", channelId ?? null, tenantId ?? null],
    queryFn: () => subscriptionsApi.list(channelId, tenantId),
  });
}

export function useAlertEvents(q?: AlertEventsQuery) {
  return useQuery({
    queryKey: alertQk.events(q),
    queryFn: () => alertEventsApi.list(q),
  });
}

/**
 * Ocorrências agrupadas por problema.
 *
 * `staleTime` curto: esta tela é consultada logo depois de um alerta chegar
 * no celular, e mostrar estado velho aí é pior do que a consulta extra.
 */
export function useEpisodios(q?: EpisodiosQuery) {
  return useQuery({
    queryKey: ["alert-episodios", q],
    queryFn: () => episodiosApi.list(q),
    staleTime: 10_000,
  });
}

export function useEpisodio(id: string | null) {
  return useQuery({
    queryKey: ["alert-episodio", id],
    queryFn: () => episodiosApi.get(id!),
    enabled: !!id,
  });
}

export function useActiveAlerts(tenantId?: string) {
  return useQuery({
    queryKey: alertQk.active(tenantId),
    queryFn: () => alertEventsApi.active(tenantId),
    // 10s — alertas precisam ser percebidos rápido pelo operador. Mantido
    // curto de propósito; o backend cacheia por 5s então 2 de cada 3 hits
    // não vão pro DB.
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
}

/**
 * Sites em queda geral de energia (#162 Camada 2).
 * Retorna [] se nenhum site em firing — o widget deve esconder ao invés
 * de renderizar bloco vazio.
 */
export function useActiveSiteAlerts(tenantId?: string) {
  return useQuery({
    queryKey: alertQk.activeSite(tenantId),
    queryFn: () => alertEventsApi.activeSiteAlerts(tenantId),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
}

export function useDeviceHealth(deviceId: string | undefined) {
  return useQuery({
    queryKey: deviceId ? alertQk.deviceHealth(deviceId) : ["device-health", "noop"],
    queryFn: () => deviceHealthApi.get(deviceId!),
    enabled: !!deviceId,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

export function useDeviceStatuses(filters?: DeviceStatusFilters) {
  return useQuery({
    queryKey: ["device-statuses", filters ?? {}],
    queryFn: () => deviceHealthApi.list(filters),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

// --- Cadastro Comercial -----------------------------------------------------

export function useTenantCompany(tenantId: string | undefined) {
  return useQuery({
    queryKey: tenantId ? qk.tenantCompany(tenantId) : ["tenant", "noop", "company"],
    queryFn: () => tenantsCompanyApi.getCompany(tenantId!),
    enabled: !!tenantId,
  });
}

export function useTenantResponsaveis(
  tenantId: string | undefined,
  includeInactive = false,
) {
  return useQuery({
    queryKey: tenantId
      ? qk.tenantResponsaveis(tenantId, includeInactive)
      : ["tenant", "noop", "responsaveis"],
    queryFn: () => tenantsCompanyApi.listResponsaveis(tenantId!, includeInactive),
    enabled: !!tenantId,
  });
}

export function useTenantLicencas(
  tenantId: string | undefined,
  includeInactive = false,
) {
  return useQuery({
    queryKey: tenantId
      ? qk.tenantLicencas(tenantId, includeInactive)
      : ["tenant", "noop", "licencas"],
    queryFn: () => tenantsCompanyApi.listLicencas(tenantId!, includeInactive),
    enabled: !!tenantId,
  });
}

export function useTenantCompleteness(tenantId: string | undefined) {
  return useQuery({
    queryKey: tenantId
      ? qk.tenantCompleteness(tenantId)
      : ["tenant", "noop", "completeness"],
    queryFn: () => tenantsCompanyApi.getCompleteness(tenantId!),
    enabled: !!tenantId,
    // Refetch quando volta foco (depois de salvar campos)
    refetchOnWindowFocus: true,
  });
}

export function useTenantAuditLogs(
  tenantId: string | undefined,
  limit = 100,
  enabled = true,
) {
  return useQuery({
    queryKey: tenantId ? qk.tenantAuditLogs(tenantId) : ["tenant", "noop", "audit"],
    queryFn: () => tenantsCompanyApi.listAuditLogs(tenantId!, limit),
    enabled: !!tenantId && enabled,
  });
}

/**
 * Lookup ViaCEP. Usado on-demand (não dispara automaticamente).
 *
 * Uso típico:
 *   const lookup = useCepLookup();
 *   const result = await lookup.mutateAsync("01310-100");
 *
 * Optei por mutation pq não queremos cachear via TanStack — o cache do
 * backend (httpx) já cobre repeats.
 */
export function useCepLookup() {
  return {
    lookup: (cep: string) => tenantsCompanyApi.lookupCep(cep),
  };
}

// --- Reports ----------------------------------------------------------------

export function useReports(filters?: ReportListFilters) {
  return useQuery({
    queryKey: qk.reports(filters),
    queryFn: () => reportsApi.list(filters),
    refetchOnWindowFocus: true,
  });
}

/**
 * Single report. Pra polling: passa `pollWhilePending=true` que faz
 * refetch a cada 2s enquanto status estiver pending|running.
 */
export function useReport(id: string | undefined, pollWhilePending = false) {
  return useQuery({
    queryKey: id ? qk.report(id) : ["report", "noop"],
    queryFn: () => reportsApi.get(id!),
    enabled: !!id,
    refetchInterval: pollWhilePending
      ? (q) => {
          const data = q.state.data;
          if (!data) return 2000;
          const s = (data as { status: string }).status;
          return s === "pending" || s === "running" ? 2000 : false;
        }
      : false,
  });
}
